// The viewport `layer` commands a loaded sim turns on, by contract: hemo vessels and particles (or a
// tissue-fate volume), dynamics cortical activity, cells arbors, pharma's engagement on the cortex. Names
// follow viewport-api-v0 and cells-sim-v0.

import type { Command } from '../../viewport/api.ts';
import { colormap, type RampName } from '../../ui/scales.ts';
import type { Overlay } from './overlay.ts';
import type { CellsHeader, CommonHeader, DynamicsHeader, PharmaHeader, SimFormat } from './types.ts';
import { isCircuitKind } from './circuit.ts';

export type LayerCommand = Extract<Command, { cmd: 'layer' }>;

/** Particles drawn along the vessels of a 1D network by default (the engine may cap it). */
export const PARTICLE_COUNT = 12000;

/** The most particles the Particles section's density asks for: at its default 60 %, PARTICLE_COUNT. */
export const PARTICLE_MAX = 20000;

/** How many particles a density (a share of PARTICLE_MAX) asks for. */
export const particleCount = (density: number) => Math.round(PARTICLE_MAX * Math.min(1, Math.max(0, density)));

/** Membrane voltage on the activity ramp, mV: rest sits near its floor, dendritic plateaus go amber, spikes white. */
export const MEMBRANE_MV: [number, number] = [-80, 0];

/** The same for a circuit, mV, drawn at the scale of its column, where an arbor is a pixel or two across:
 *  narrower, so a spike and its backpropagation glow in full while the membrane between stays dark. */
export const CIRCUIT_MV: [number, number] = [-60, -20];

const stem = (p: string) => /([^/]+?)(\.[^./]+)?$/.exec(p)?.[1] ?? p;

/** An overlay's dose ratio at time `t`, s: linear between its frames, held past either end. */
export function doseRatioAt(o: Pick<Overlay, 'times' | 'doseRatio'>, t: number): number {
  const r = o.doseRatio;
  const T = o.times;
  if (!r?.length || T.length !== r.length) return 1;
  if (t <= T[0]!) return r[0]!;
  if (t >= T[T.length - 1]!) return r[r.length - 1]!;
  let i = 1;
  while (T[i]! < t) i++;
  const a = (t - T[i - 1]!) / (T[i]! - T[i - 1]!);
  return r[i - 1]! + a * (r[i]! - r[i - 1]!);
}

/** Three significant figures: a layer whose range follows the clock goes again only when it moves that much. */
const sig3 = (x: number) => Number(x.toPrecision(3));

/** The pharma target a scene shows: the one picked (the Receptors section, the traces) where the run models
 *  it, else the run's primary. */
export function pharmaTarget(header: CommonHeader, picked?: string): string | undefined {
  const h = header as PharmaHeader;
  return picked && h.target_codes?.includes(picked) ? picked : h.primary_target;
}

/** `time` is the scene's, s: pharma's range follows a rescaled course's ratio along it. `target` is the
 *  pharma target picked that the overlays were made for (`overlaysTarget`, then `pharmaTarget`), so the
 *  tint changes with them: a receptor just picked at another dose waits for its own ratio rather than
 *  drawing at the course's dose for a moment. `ramp` is the one the slot's scene names (SimSlotSpec.ramp),
 *  which a dynamics run's activity and a cells run's arbors take in place of the one their kind gives. */
export function layersFor(slot: string, format: SimFormat, header: CommonHeader, overlay?: Overlay, time = 0, target?: string, ramp?: RampName): LayerCommand[] {
  switch (format) {
    case 'hemo': {
      if (header.kind === '1d-network') {
        const q = overlay?.quantity ?? 'velocity';
        return [
          { cmd: 'layer', name: 'vessels', on: true, slot, quantity: q, colormap: colormap(overlay?.ramp ?? 'velocity'), ...(overlay ? { range: overlay.range } : {}) },
          { cmd: 'layer', name: 'particles', on: true, slot, count: PARTICLE_COUNT },
        ];
      }
      const vols = ((header.files?.volumes as string[] | undefined) ?? []).map(stem);
      const state = vols.find((v) => v === 'tissue-state') ?? vols.find((v) => /cbf|tmax|p-infarct/.test(v));
      return state ? [{ cmd: 'layer', name: `volume:${state}`, on: true, slot }] : [];
    }
    case 'dynamics': {
      const range = (header as DynamicsHeader).display?.activity_range ?? overlay?.range;
      // A spreading depolarization on design's cold scale, so it reads apart from a seizure's ember.
      const r = ramp ?? (header.kind === 'spreading-depolarization' ? 'sd' : 'activity');
      return [{ cmd: 'layer', name: 'activity', on: true, slot, colormap: colormap(r), ...(range ? { range } : {}) }];
    }
    case 'cells': {
      const v = (header as CellsHeader).series?.find((s) => s.quantity === 'v');
      const range = v && (v.units ?? 'mV') === 'mV' ? (isCircuitKind(header.kind) ? CIRCUIT_MV : MEMBRANE_MV) : v?.range;
      return [
        { cmd: 'layer', name: 'arbors', on: true, slot, quantity: 'v', colormap: colormap(ramp ?? 'activity'), ...(range ? { range } : {}) },
        { cmd: 'layer', name: 'somata', on: true, slot },
        ...(typeof header.files?.synapses === 'string' && header.files.synapses ? [{ cmd: 'layer' as const, name: 'synapses', on: true, slot }] : []),
      ];
    }
    case 'pharma': {
      // Engagement on the cortex (engines/wgpu #626): the target picked, else the run's primary; off for
      // one it has no cortex map for. On the fixed 0–1 scale (#644), so the colour comes from the
      // engagement itself, as on design's Drugs screen. At another dose the engine's course (the run's own)
      // is scaled by the target's occupancy ratio r through the range: e × r over [0, 1] is e over
      // [0, 1/r]. Where r is 0 (before the dose) every value is 0, so the range doesn't matter.
      const h = header as PharmaHeader;
      const t = pharmaTarget(header, target);
      const mapped = h.spatial_target_codes ?? h.target_codes;
      if (!t || (mapped && !mapped.includes(t))) return [{ cmd: 'layer', name: 'activity', on: false, slot }];
      const r = overlay && overlay.target === t ? doseRatioAt(overlay, time) : 1;
      return [{ cmd: 'layer', name: 'activity', on: true, slot, quantity: t, range: [0, r > 0 ? sig3(1 / r) : 1] }];
    }
    case 'stim':
      // The engine never reads a stim run: the app pushes its field as vertex data (ba-engine's live
      // vertex layer, #2), so it has no sim layers.
      return [];
    case 'lifetime':
      // Lifetime's slot is app-only: the scene's own drive loads the sequence into its own slot and sends the
      // `morph` layer lit by the panel's map (features/lifetime.ts), as the 3D flow view drives its lumen.
      return [];
    case 'sections':
      // Closed 2D sections the app draws itself (Lifetime's model track): no viewport reads the format.
      return [];
    case 'thickness-sections':
      // LIFE-020's assigned-thickness sections, the same: drawn in the strip at a growth of their own (Lifetime's Assigned thickness track).
      return [];
  }
}
